import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getUserHomePath, getUserPostsPath } from '../../shared/config/env';
import { Category, Information } from '../../shared/types/header';
import { useLanguage } from '../../shared/hooks/useLanguage';
import { getPosts } from '../../shared/api/posts';
import { Post } from '../../shared/types/post';
import { getAnnouncementCategoryName } from '../../shared/config/constants';
import SettingsButton from '../../shared/components/SettingsButton';
import './SiteHeader.css';

interface SiteHeaderProps {
  blogName?: string;
  blogNameEn?: string;
  categories?: Category[];
  informations?: Information[];
}

/**
 * サイトヘッダーコンポーネント
 * Qiitaを参考にしたデザイン
 */
const SiteHeader: React.FC<SiteHeaderProps> = ({
  blogName = 'Version0.1',
  blogNameEn = 'Version0.1',
  categories = [],
  informations = [],
}) => {
  const lang = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const homePath = getUserHomePath();
  const userPostsPath = getUserPostsPath();
  const [announcements, setAnnouncements] = useState<Post[]>([]);
  const [visibleAnnouncements, setVisibleAnnouncements] = useState<Post[]>([]);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const lastScrollY = useRef<number>(0);
  const [latestVersion, setLatestVersion] = useState<string>('');
  const [isHovered, setIsHovered] = useState<boolean>(false);
  
  // 言語に応じたブログ名を取得
  const displayBlogName = lang === 'ja' ? blogName : (blogNameEn || blogName);
  
  // ブログ名から「Version」と「0.1」を分離
  const blogNameMatch = displayBlogName.match(/^(Version)(.*)$/);
  const blogNamePrefix = blogNameMatch ? blogNameMatch[1] : displayBlogName;
  const blogNameVersion = blogNameMatch ? blogNameMatch[2] : '';
  
  // 最新バージョンを取得
  useEffect(() => {
    const fetchLatestVersion = async () => {
      try {
        const response = await fetch('/diagrams/diagrams-manifest.json');
        if (!response.ok) {
          return;
        }
        
        const manifest = await response.json();
        const versions: Array<{ version: string; filename: string }> = manifest.diagrams || [];
        
        if (versions.length === 0) {
          return;
        }
        
        // バージョン番号を比較する関数（セマンティックバージョニング対応）
        const compareVersions = (a: string, b: string): number => {
          const aParts = a.split('.').map(Number);
          const bParts = b.split('.').map(Number);
          const maxLength = Math.max(aParts.length, bParts.length);
          
          for (let i = 0; i < maxLength; i++) {
            const aPart = aParts[i] || 0;
            const bPart = bParts[i] || 0;
            if (aPart > bPart) return -1; // 新しい順（降順）
            if (aPart < bPart) return 1;
          }
          return 0;
        };
        
        // バージョンでソート（新しい順）
        const sortedVersions = versions.sort((a, b) => compareVersions(a.version, b.version));
        setLatestVersion(sortedVersions[0].version);
      } catch (err) {
        // エラーは無視
      }
    };
    
    fetchLatestVersion();
  }, []);

  // 表示可能なカテゴリーを取得
  const visibleCategories = categories.filter((cat) => cat.isVisible);

  // 表示可能なインフォメーションを優先度順に取得
  const visibleInformations = informations
    .filter((info) => info.isVisible)
    .sort((a, b) => a.priority - b.priority);

  // お知らせを取得（最新2件）
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    
    const fetchAnnouncements = async () => {
      try {
        const response = await getPosts({ status: 'published', lang, per_page: 50, skipAuth: true });
        const categoryName = getAnnouncementCategoryName(lang);
        const announcementPosts = response.posts.filter(
          (post) => post.category === categoryName
        );
        
        // 最新2件を取得して逆順にする（古い方から新しい方の順）
        const latestAnnouncements = announcementPosts.slice(0, 2).reverse();
        setAnnouncements(latestAnnouncements);
        
        // 1件目（古い方）を先に表示
        if (latestAnnouncements.length > 0) {
          setVisibleAnnouncements([latestAnnouncements[0]]);
          
          // 2件目（新しい方）を少し遅延させて表示（アニメーションなし）
          if (latestAnnouncements.length > 1) {
            timeoutId = setTimeout(() => {
              setVisibleAnnouncements(latestAnnouncements);
            }, 300); // 300ms遅延
          }
        } else {
          setVisibleAnnouncements([]);
        }
      } catch (err) {
        // お知らせ取得エラーは無視
        setVisibleAnnouncements([]);
      }
    };

    setVisibleAnnouncements([]); // リセット
    fetchAnnouncements();
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [lang]);

  // お知らせクリック時の処理
  const handleAnnouncementClick = (e: React.MouseEvent, announcement: Post) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/${lang}${userPostsPath}/${announcement.slug}`);
  };

  // スクロール監視（スマホ版の全ページ）
  useEffect(() => {
    const handleScroll = () => {
      // スマホ版（768px以下）のみ適用
      if (window.innerWidth > 768) {
        setIsScrolled(false);
        return;
      }

      const currentScrollY = window.scrollY;
      const scrollDownThreshold = 100; // 下にスクロールするときの閾値（100px以上で非表示）
      const scrollUpThreshold = 50; // 上にスクロールするときの閾値（50px以下で表示）

      // ヒステリシスを追加して点滅を防ぐ
      if (currentScrollY > lastScrollY.current) {
        // 下にスクロール
        if (currentScrollY > scrollDownThreshold) {
          setIsScrolled(true);
        }
      } else if (currentScrollY < lastScrollY.current) {
        // 上にスクロール
        if (currentScrollY < scrollUpThreshold) {
          setIsScrolled(false);
        }
      } else {
        // スクロール位置が変わらない場合（初期状態など）
        if (currentScrollY < scrollUpThreshold) {
          setIsScrolled(false);
        } else if (currentScrollY > scrollDownThreshold) {
          setIsScrolled(true);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    // リサイズ時にもチェック
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);



  return (
    <header className={`site-header ${isScrolled ? 'site-header--scrolled' : ''}`}>
      <div className="site-header__container">
        {/* 設定ボタン（右上固定） */}
        <div className="site-header__settings">
          <SettingsButton />
        </div>

        {/* 上部：ブログ名 */}
        <div className={`site-header__top ${isScrolled ? 'site-header__top--hidden' : ''}`}>
          <div className="site-header__logo">
            <div className="site-header__logo">
              <Link 
                to={`/${lang}${homePath === '/' ? '' : homePath}`} 
                className="site-header__logo-link"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
              >
                <span className="site-header__logo-title">
                  {blogNameVersion ? (
                    <>
                      {blogNamePrefix}
                      <span className="site-header__logo-version-number">
                        {isHovered && latestVersion ? latestVersion : blogNameVersion}
                      </span>
                    </>
                  ) : (
                    displayBlogName
                  )}
                </span>
              </Link>
            </div>
            <div className="site-header__logo-logs">
              <span className="site-header__logo-tagline">
                <span className="site-header__logo-tagline-level">[warn]</span>
                <span className="site-header__logo-tagline-message">
                  {lang === 'ja' ? 'やりたいところだけ実装するので永遠に完成しません' : 'Only implementing what I want, so it will never be completed'}
                </span>
              </span>
              {visibleAnnouncements.map((announcement) => (
                <div 
                  key={announcement.post_id}
                  className="site-header__announcement-log"
                  onClick={(e) => handleAnnouncementClick(e, announcement)}
                >
                  <span className="site-header__announcement-log-level">[info]</span>
                  <span className="site-header__announcement-log-message">
                    {announcement.title}
                  </span>
                </div>
              ))}
              {/* 2件目が表示されていない場合でもスペースを確保 */}
              {announcements.length === 2 && visibleAnnouncements.length === 1 && (
                <div className="site-header__announcement-log site-header__announcement-log--placeholder" aria-hidden="true">
                  <span className="site-header__announcement-log-level">[info]</span>
                  <span className="site-header__announcement-log-message">
                    {announcements[1].title}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* 中部：カテゴリー */}
        {visibleCategories.length > 0 && (
          <nav className="site-header__nav">
            <ul className="site-header__nav-list">
              {visibleCategories.map((category) => {
                const displayName = lang === 'ja' ? category.name : (category.nameEn || category.name);
                // 現在のパスとクエリパラメータを確認して、このカテゴリーが選択されているか判定
                const isActive = location.pathname === `/${lang}${homePath === '/' ? '' : homePath}` && 
                                 new URLSearchParams(location.search).get('category') === category.slug;
                return (
                  <li key={category.id} className="site-header__nav-item">
                    <Link
                      to={`/${lang}${homePath === '/' ? '' : homePath}?category=${category.slug}`}
                      className={`site-header__nav-link ${isActive ? 'site-header__nav-link--active' : ''}`}
                    >
                      {displayName}
                    </Link>
                  </li>
                );
              })}
              {/* Aboutタブをカテゴリーの末尾に追加 */}
              <li className="site-header__nav-item">
                <Link
                  to={`/${lang}/about`}
                  className={`site-header__nav-link ${location.pathname === `/${lang}/about` ? 'site-header__nav-link--active' : ''}`}
                >
                  About
                </Link>
              </li>
              {/* アプリ */}
              <li className="site-header__nav-item">
                <Link
                  to={`/${lang}/apps`}
                  className={`site-header__nav-link ${location.pathname.startsWith(`/${lang}/apps`) ? 'site-header__nav-link--active' : ''}`}
                >
                  {lang === 'ja' ? 'アプリ' : 'Apps'}
                </Link>
              </li>
            </ul>
          </nav>
        )}

        {/* 下部：インフォメーション */}
        {visibleInformations.length > 0 && (
          <div className={`site-header__info ${isScrolled ? 'site-header__info--hidden' : ''}`}>
            {visibleInformations.map((info) => {
              const displayMessage = lang === 'ja' ? info.message : (info.messageEn || info.message);
              const displayLinkText = lang === 'ja' ? info.linkText : (info.linkTextEn || info.linkText);
              // リンクURLが相対パスの場合、言語パスを追加
              const linkUrl = info.linkUrl?.startsWith('/') && !info.linkUrl.startsWith('/admin')
                ? `/${lang}${info.linkUrl}`
                : info.linkUrl;
              
              return (
                <div key={info.id} className="site-header__info-item">
                  {linkUrl ? (
                    <a
                      href={linkUrl}
                      className="site-header__info-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {displayMessage}
                      {displayLinkText && (
                        <span className="site-header__info-link-text">
                          {' '}
                          {displayLinkText}
                        </span>
                      )}
                    </a>
                  ) : (
                    <span className="site-header__info-text">
                      {displayMessage}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </header>
  );
};

export default SiteHeader;

